import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { ProjectMethodologyEnum } from 'gql/graphql';

import { useMemo } from 'react';

import { bimContractOtherCostForUpdateGql, uerContractOtherCostForUpdateGql } from '../editOtherQueries';

interface IUseContractOtherCostForUpdate {
    contractOtherCostId: number;
    contractId: number;
    methodology: ProjectMethodologyEnum;
    constructionTypeId?: number;
}

const requestIdUer = 'uerContractOtherCostForUpdate';
const requestIdBim = 'bimContractOtherCost';

export const useContractOtherCostForUpdate = (props: IUseContractOtherCostForUpdate) => {
    const { contractId, contractOtherCostId, methodology, constructionTypeId } = props;

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const otherForUpdateVariables = useMemo(
        () => ({
            contractId,
            contractOtherCostId,
            ...(isUer && { editConstructionTypeId: constructionTypeId }),
        }),
        [contractId, contractOtherCostId, isUer, constructionTypeId],
    );

    const [otherForUpdateData, otherForUpdateStatus] = useRequest({
        requestId: requestIdUer,
        variables: otherForUpdateVariables,
        query: uerContractOtherCostForUpdateGql,
        withEffect: isUer && !!(contractId && contractOtherCostId),
    });

    const [bimOtherForUpdateData, bimOtherForUpdateStatus] = useRequest({
        requestId: requestIdBim,
        variables: otherForUpdateVariables,
        query: bimContractOtherCostForUpdateGql,
        withEffect: !isUer && !!(contractId && contractOtherCostId),
    });

    const [resetUer] = useResetStoreData({ requestId: requestIdUer });

    const [resetBim] = useResetStoreData({ requestId: requestIdBim });

    const data = useMemo(
        () => (isUer ? otherForUpdateData?.uerContractOtherCostForUpdate : bimOtherForUpdateData?.bimContractOtherCost),
        [bimOtherForUpdateData?.bimContractOtherCost, isUer, otherForUpdateData?.uerContractOtherCostForUpdate],
    );

    const status = useMemo(
        () => (isUer ? otherForUpdateStatus : bimOtherForUpdateStatus),
        [bimOtherForUpdateStatus, isUer, otherForUpdateStatus],
    );

    const reset = useMemo(() => (isUer ? resetUer : resetBim), [isUer, resetBim, resetUer]);

    return {
        reset,
        data,
        status,
    };
};
